/* import __COLOCATED_TEMPLATE__ from './destination-file-browser.hbs'; */
import Component from '@glimmer/component';
import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import { inject as service } from '@ember/service';

export default class DestinationFileBrowserComponent extends Component
{
	@service uploaderAjax;
	@service confirmation;

	@tracked objects = [];
	@tracked isLoading = false;
	@tracked isUploading = false;
	@tracked errorMessage = null;

	constructor()
	{
		super(...arguments);
		this.refreshListing();
	}

	get destinationId()
	{
		return this.args.destination?.id;
	}

	async refreshListing()
	{
		if(!this.destinationId) return;
		this.isLoading = true;
		this.errorMessage = null;
		try
		{
			const response = await this.uploaderAjax.request("/destinations/"+this.destinationId+"/objects");
			if(!this.isDestroying && !this.isDestroyed)
			{
				this.objects = response.objects || [];
			}
		}
		catch(error)
		{
			console.error('[DestinationFileBrowser] list failed:', error);
			if(!this.isDestroying && !this.isDestroyed)
			{
				this.objects = [];
				this.errorMessage = error.message || 'Failed to list bucket';
			}
		}
		finally
		{
			if(!this.isDestroying && !this.isDestroyed)
			{
				this.isLoading = false;
			}
		}
	}

	@action
	async filesChanged(browserEvent)
	{
		const files = Array.from(browserEvent.target.files || []);
		if(files.length === 0) return;

		this.isUploading = true;
		this.errorMessage = null;
		try
		{
			// Sequential so websocket log lines stay ordered and the backend
			// isn't fighting itself for disk on big uploads.
			for(const file of files)
			{
				const formData = new FormData();
				formData.append('file', file, file.name);
				try
				{
					await this.uploaderAjax.multipart("/destinations/"+this.destinationId+"/objects", formData);
				}
				catch(error)
				{
					console.error('[DestinationFileBrowser] upload failed for '+file.name+':', error);
					this.errorMessage = 'Upload failed for '+file.name+': '+(error.message||error);
				}
			}
		}
		finally
		{
			// Clear the picker so re-selecting the same file fires change again.
			browserEvent.target.value = '';
			this.isUploading = false;
			await this.refreshListing();
		}
	}

	@action
	async deleteObject(object)
	{
		const confirmed = await this.confirmation.confirm({
			title: 'Delete File',
			message: `Are you sure you want to delete "${object.Key}" from "${this.args.destination.name}"?`,
			confirmLabel: 'Delete',
			confirmStyle: 'danger'
		});
		if(!confirmed) return;

		try
		{
			await this.uploaderAjax.request(
				"/destinations/"+this.destinationId+"/objects/"+encodeURIComponent(object.Key),
				{ method: 'DELETE' }
			);
		}
		catch(error)
		{
			console.error('[DestinationFileBrowser] delete failed:', error);
			this.errorMessage = 'Delete failed for '+object.Key+': '+(error.message||error);
		}
		await this.refreshListing();
	}

	@action
	clickUploadInput()
	{
		// Reset the value first so re-picking the same file still triggers change.
		const input = document.getElementById('destination-file-browser-input-'+this.destinationId);
		if(input)
		{
			input.value = '';
			input.click();
		}
	}

	formatSize(bytes)
	{
		if(bytes == null) return '';
		if(bytes < 1024) return bytes + ' B';
		if(bytes < 1024*1024) return (bytes/1024).toFixed(1) + ' KB';
		if(bytes < 1024*1024*1024) return (bytes/1024/1024).toFixed(1) + ' MB';
		return (bytes/1024/1024/1024).toFixed(2) + ' GB';
	}

	formatDate(d)
	{
		if(!d) return '';
		const date = new Date(d);
		return isNaN(date.getTime()) ? '' : date.toLocaleString();
	}

	@action
	sizeOf(object) { return this.formatSize(object.Size); }

	@action
	dateOf(object) { return this.formatDate(object.LastModified); }
}
